import { Link } from "@tanstack/react-router";
import { Bot, KeyRound, LogOut, Menu, Monitor, Moon, Search, Sun, X } from "lucide-react";
import { useEffect, useState, type ComponentType, type ReactNode } from "react";
import { cx } from "./cx";
import { Iniciais } from "./Iniciais";
import { aplicarTema, lerTema, type Tema } from "./tema";

export type ItemMenu = {
  para: string;
  rotulo: string;
  icone: ComponentType<{ size?: number; "aria-hidden"?: boolean }>;
  contagem?: number;
  exato?: boolean;
};

export type Pessoa = { nome: string; email: string; perfil: string };

/**
 * A casca (referências: Shakuro, Notion, Twenty). Altura da janela, e nada rola na página: rola o conteúdo.
 * Computador (≥1024): menu lateral de 240 px, do mesmo tom do fundo. Abaixo disso: barra no topo e o menu
 * vira gaveta. `min-w-0` em toda a cadeia, senão uma tabela larga empurra o menu para fora.
 */
export function MolduraBase({
  itens, pessoa, aoBuscar, aoSair, painel, children,
}: {
  itens: ItemMenu[];
  pessoa?: Pessoa | null;
  aoBuscar: () => void;
  aoSair?: () => void;
  /** Painel lateral aberto (tarefa, assistente). */
  painel?: ReactNode;
  children: ReactNode;
}) {
  const [gaveta, setGaveta] = useState(false);

  useEffect(() => {
    if (!gaveta) return;
    const tecla = (e: KeyboardEvent) => e.key === "Escape" && setGaveta(false);
    window.addEventListener("keydown", tecla);
    return () => window.removeEventListener("keydown", tecla);
  }, [gaveta]);

  const menu = (
    <Menu_ itens={itens} pessoa={pessoa} aoBuscar={() => { setGaveta(false); aoBuscar(); }} aoSair={aoSair}
      aoNavegar={() => setGaveta(false)} />
  );

  return (
    <div className="flex h-dvh overflow-hidden bg-fundo">
      <nav aria-label="Principal" className="hidden w-56 shrink-0 fio-r lg:flex">{menu}</nav>

      {gaveta && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <button type="button" aria-label="Fechar menu" onClick={() => setGaveta(false)} className="absolute inset-0 cursor-default bg-texto-1/30" />
          <nav aria-label="Principal" className="relative flex h-full w-72 max-w-[85vw] bg-fundo shadow-flutuante"> {/* design-ok: largura relativa à janela */}
            {menu}
            <button type="button" onClick={() => setGaveta(false)}
              className="absolute top-3 right-3 grid size-controle cursor-pointer place-items-center rounded-controle text-texto-3 hover:bg-superficie-2">
              <X size={16} aria-hidden /><span className="sr-only">Fechar menu</span>
            </button>
          </nav>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-14 shrink-0 items-center gap-2 fio-b bg-fundo px-2 lg:hidden">
          <button type="button" onClick={() => setGaveta(true)}
            className="grid size-11 cursor-pointer place-items-center rounded-controle text-texto-2 hover:bg-superficie-2">
            <Menu size={20} aria-hidden /><span className="sr-only">Abrir menu</span>
          </button>
          <span className="flex-1 font-medium text-texto-1">F&amp;A Gestão</span>
          <button type="button" onClick={aoBuscar}
            className="grid size-11 cursor-pointer place-items-center rounded-controle text-texto-2 hover:bg-superficie-2">
            <Search size={20} aria-hidden /><span className="sr-only">Buscar</span>
          </button>
        </header>
        <div className="flex min-h-0 flex-1">
          <main className="min-w-0 flex-1 overflow-y-auto px-4 py-6 md:px-6 lg:px-8">{children}</main>
          {painel}
        </div>
      </div>
    </div>
  );
}

function Menu_({
  itens, pessoa, aoBuscar, aoSair, aoNavegar,
}: {
  itens: ItemMenu[]; pessoa?: Pessoa | null; aoBuscar: () => void; aoSair?: () => void; aoNavegar: () => void;
}) {
  return (
    <div className="flex min-h-0 w-full flex-col p-3">
      <div className="flex h-controle items-center gap-2 px-2">
        <span aria-hidden className="grid size-6 place-items-center rounded-controle bg-acao font-medium text-acao-texto">F</span>
        <span className="font-medium text-texto-1">F&amp;A Gestão</span>
      </div>

      <button type="button" onClick={aoBuscar}
        className="mt-3 flex h-controle cursor-pointer items-center gap-2 rounded-controle bg-superficie-2 px-2 text-texto-2 hover:text-texto-1">
        <Search size={14} aria-hidden />
        <span className="flex-1 text-left">Buscar</span>
        <kbd className="font-sans text-meta text-texto-3">Ctrl K</kbd>
      </button>

      <ul className="mt-3 flex flex-col">
        {itens.map((i) => (
          <li key={i.para}>
            <Link to={i.para} onClick={aoNavegar} activeOptions={{ exact: i.exato }}
              className="group flex h-controle items-center gap-2 rounded-controle px-2 text-texto-2 hover:bg-superficie-2 hover:text-texto-1"
              activeProps={{ className: "!bg-superficie-2 !text-texto-1 font-medium" }}>
              <i.icone size={14} aria-hidden />
              <span className="flex-1 truncate">{i.rotulo}</span>
              {i.contagem !== undefined && i.contagem > 0 && (
                <span className="numeros text-meta text-texto-3">{i.contagem > 99 ? "99+" : i.contagem}</span>
              )}
            </Link>
          </li>
        ))}
      </ul>

      <div className="mt-3 fio-t pt-3">
        <span className="flex h-controle items-center gap-2 rounded-controle px-2 text-texto-2" title="Chega no E5">
          <Bot size={14} aria-hidden />
          <span className="flex-1">Assistente</span>
          <span className="text-meta">em breve</span>
        </span>
      </div>

      <div className="mt-auto pt-3">
        <MenuPessoa pessoa={pessoa} aoSair={aoSair} aoNavegar={aoNavegar} />
      </div>
    </div>
  );
}

function MenuPessoa({ pessoa, aoSair, aoNavegar }: { pessoa?: Pessoa | null; aoSair?: () => void; aoNavegar: () => void }) {
  const [tema, setTema] = useState<Tema>(lerTema);
  const temas: { v: Tema; rotulo: string; icone: ReactNode }[] = [
    { v: "sistema", rotulo: "Sistema", icone: <Monitor size={14} aria-hidden /> },
    { v: "claro", rotulo: "Claro", icone: <Sun size={14} aria-hidden /> },
    { v: "escuro", rotulo: "Escuro", icone: <Moon size={14} aria-hidden /> },
  ];
  return (
    <details className="group relative">
      <summary className="flex h-10 cursor-pointer list-none items-center gap-2 rounded-controle px-2 hover:bg-superficie-2 [&::-webkit-details-marker]:hidden">
        <Iniciais nome={pessoa?.nome ?? "?"} />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-texto-1">{pessoa?.nome ?? "…"}</span>
          <span className="block truncate text-meta text-texto-3">{pessoa?.perfil}</span>
        </span>
      </summary>
      <div className="absolute bottom-full left-0 z-10 mb-1 w-full min-w-56 rounded-cartao bg-flutuante p-1 shadow-flutuante">
        {pessoa && <p className="truncate px-2 py-2 text-meta text-texto-3">{pessoa.email}</p>}
        <div role="radiogroup" aria-label="Tema" className="mb-1 grid grid-cols-3 gap-1 rounded-controle bg-superficie-2 p-1">
          {temas.map((t) => (
            <button key={t.v} type="button" role="radio" aria-checked={tema === t.v}
              onClick={() => { aplicarTema(t.v); setTema(t.v); }}
              className={cx("flex h-6 cursor-pointer items-center justify-center gap-1 rounded-controle text-meta font-medium text-texto-3 hover:text-texto-1",
                tema === t.v && "bg-superficie")}>
              {t.icone}<span className="sr-only sm:not-sr-only">{t.rotulo}</span>
            </button>
          ))}
        </div>
        <Link to="/conta/tokens" onClick={aoNavegar}
          className="flex h-controle items-center gap-2 rounded-controle px-2 text-texto-2 hover:bg-superficie-2 hover:text-texto-1">
          <KeyRound size={14} aria-hidden /> Tokens pessoais
        </Link>
        {aoSair && (
          <button type="button" onClick={aoSair}
            className="flex h-controle w-full cursor-pointer items-center gap-2 rounded-controle px-2 text-texto-2 hover:bg-superficie-2 hover:text-texto-1">
            <LogOut size={14} aria-hidden /> Sair
          </button>
        )}
      </div>
    </details>
  );
}
